import type { SubscriptionTier } from "@/hooks/use-auth";

export interface Plan {
  id: SubscriptionTier;
  name: string;
  price: number;
  limit: number | null; // null = unlimited
  tagline: string;
  features: string[];
  highlight?: boolean;
}

export const PLANS: Plan[] = [
  {
    id: "free",
    name: "Free",
    price: 0,
    limit: 1,
    tagline: "Try the platform on your first client.",
    features: ["1 project total", "Payment Protection on every milestone", "Client portal, chat & files"],
  },
  {
    id: "starter",
    name: "Starter",
    price: 9.99,
    limit: 5,
    tagline: "For freelancers landing steady work.",
    features: ["5 projects / month", "Priority email support", "Standard payout release"],
  },
  {
    id: "pro",
    name: "Pro",
    price: 19.99,
    limit: 15,
    tagline: "For pros running a full pipeline.",
    features: ["15 projects / month", "Advanced analytics", "Faster payout release"],
    highlight: true,
  },
  {
    id: "unlimited",
    name: "Unlimited",
    price: 34.99,
    limit: null,
    tagline: "For agencies and heavy users.",
    features: ["Unlimited projects", "Dedicated support", "Team seats (soon)"],
  },
];

export function planFor(tier: SubscriptionTier): Plan {
  return PLANS.find((p) => p.id === tier) ?? PLANS[0];
}

export function effectiveLimit(tier: SubscriptionTier, bonus = 0): number | null {
  const p = planFor(tier);
  return p.limit === null ? null : p.limit + (bonus || 0);
}

export function isAtLimit(tier: SubscriptionTier, used: number, bonus = 0): boolean {
  const lim = effectiveLimit(tier, bonus);
  return lim !== null && used >= lim;
}


/**
 * The tier the user actually gets right now. A paid tier whose billing period
 * has already ended (or was canceled/unpaid) falls back to "free".
 */
export function effectiveTier(
  profile:
    | {
        subscription_tier?: SubscriptionTier | null;
        subscription_status?: string | null;
        subscription_current_period_end?: string | null;
      }
    | null
    | undefined,
): SubscriptionTier {
  const tier = (profile?.subscription_tier ?? "free") as SubscriptionTier;
  if (tier === "free") return "free";
  const end = profile?.subscription_current_period_end ?? null;
  if (end) {
    const ts = new Date(end).getTime();
    if (Number.isFinite(ts) && ts < Date.now()) return "free";
  }
  const s = (profile?.subscription_status ?? "").trim().toLowerCase();
  if (["canceled", "cancelled", "unpaid", "incomplete_expired", "past_due", "expired"].includes(s)) {
    return "free";
  }
  return tier;
}

/** Statuses that count as an active paid subscription. */
export function isSubscriptionActive(
  status: string | null | undefined,
  periodEnd: string | null | undefined,
): boolean {
  const s = (status ?? "").trim().toLowerCase();
  if (s === "active" || s === "trialing") return true;
  if (s === "canceled" || s === "cancelled") {
    if (!periodEnd) return false;
    const end = new Date(periodEnd).getTime();
    return Number.isFinite(end) && end > Date.now();
  }
  return false;
}


